<template>
  <div class="mt-8" v-show="showDetails">
    <div class="">
      <dispute-notification v-if="stageDispute">
        {{ stageDispute.comment }}
      </dispute-notification>

      <el-form
        @keyup.enter.native="validateForm('kycDetailsForm')"
        @submit.native.prevent="validateForm('kycDetailsForm')"
        label-position="top"
        :model="kycDetailsForm"
        :rules="rules"
        ref="kycDetailsForm"
      >
        <div class="flex flex-col gap-1 bg-white">
          <el-form-item class="w-full" prop="accountName">
            <p>Company Name<span class="text-error-10">*</span></p>
            <el-input
              class="w-full"
              v-model="kycDetailsForm.accountName"
            ></el-input>
          </el-form-item>
          <el-form-item class="w-full">
            <p>Previous Entity Name (If Applicable)</p>
            <el-input
              class="w-full"
              v-model="kycDetailsForm.previousEntityName"
            ></el-input>
          </el-form-item>

          <div class="w-full">
            <el-form-item prop="registrationType">
              <p>Registration Type <span class="text-error-10">*</span></p>
              <el-select
                class="w-full"
                v-model="kycDetailsForm.registrationType"
                placeholder="Select"
                @change="changeCompanyType"
              >
                <el-option
                  v-for="(companyType, index) in companyTypes"
                  :key="index"
                  :label="companyType.label"
                  :value="companyType.value"
                >
                  {{ companyType.label }}
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item v-if="otherCompanyType" prop="unlistedCompanyType">
              <p>Registration Type <span class="text-error-10">*</span></p>
              <el-input
                class="w-full"
                v-model="kycDetailsForm.unlistedCompanyType"
              ></el-input>
            </el-form-item>
          </div>

          <!-- new kyc update requirement -->

          <el-form-item>
            <div class="w-full flex items-center gap-x-8">
              <p class="flex items-center gap-x-2">
                <span>Is Entity Regulated ?</span>
                <el-tooltip class="item" placement="top">
                  <template #content>
                    <p
                      style="color: white; font-weight: 200"
                      class="w-64 text-white text-center"
                    >
                      Indicate if your entity is regulated and specify the
                      regulated body if applicable
                    </p>
                  </template>
                  <p><attention-icon class="h-5 w-5" color="#9E9E9E" /></p>
                </el-tooltip>
              </p>
              <p>
                <el-radio-group v-model="kycDetailsForm.entityIsRegulated">
                  <el-radio label="yes">Yes</el-radio>
                  <el-radio label="no">No</el-radio>
                </el-radio-group>
              </p>
            </div>
          </el-form-item>
          <el-form-item
            v-if="kycDetailsForm.entityIsRegulated === 'yes'"
            prop="regulatoryBodyName"
            class="w-full"
          >
            <p>Regulatory Body Name<span class="text-error-10">*</span></p>
            <el-input
              class="w-full"
              v-model="kycDetailsForm.regulatoryBodyName"
            ></el-input>
          </el-form-item>

          <div
            class="w-full flex flex-col md:flex-row md:gap-6 md:justify-between"
          >
            <el-form-item prop="incorporationNumber" class="w-full md:w-1/2">
              <p>Incorporation Number <span class="text-error-10">*</span></p>
              <el-input
                class="w-full"
                @keydown.space.native.prevent
                v-model.trim="kycDetailsForm.incorporationNumber"
              ></el-input>
            </el-form-item>
            <el-form-item prop="incorporationDate" class="w-full md:w-1/2">
              <p>Incorporation Date <span class="text-error-10">*</span></p>
              <el-date-picker
                v-model="kycDetailsForm.incorporationDate"
                type="date"
                placeholder="YYYY-MM-DD"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                :picker-options="pickerOptions"
                full-width="fullWidth"
              />
            </el-form-item>
          </div>

          <el-form-item
            prop="clientCustomerLocation"
            @click.native="toggleClientCustomerLocation"
          >
            <p>
              In Which Countries Are Your Clients Based ?
              <span class="text-error-10">*</span>
            </p>
            <div
              class="flex items-center justify-between rounded px-4 py-2 border cursor-pointer"
            >
              <div class="flex flex-wrap gap-y-3 items-center gap-x-2">
                <span
                  v-if="kycDetailsForm.clientCustomerLocation.length === 0"
                  class="font-400 opacity-30"
                  >Select</span
                >
                <template v-else>
                  <span
                    v-for="(
                      currency, index
                    ) in kycDetailsForm.clientCustomerLocation"
                    :key="index"
                    class="flex items-center gap-x-3 rounded shadow-sm bg-gray-6 px-2.5 py-1.5 relative z-20 duration-300 transition-all hover:bg-gray-2"
                  >
                    <currency-icons
                      :currency="getCurrencyCountry(currency)"
                      size="h-5 w-5"
                    />
                    <span class="text-sm uppercase">{{
                      getCountryFullName(currency)
                    }}</span>
                    <close-icon
                      size="h-4 w-5"
                      @click.native.stop="removeLocationHandler(currency)"
                    />
                  </span>
                </template>
              </div>
              <arrow-icon
                color="#5D5E68"
                size="h-7 w-7"
                class="transform rotate-90"
                :class="{ '-rotate-90': showClientCustomerLocation }"
              />
            </div>
          </el-form-item>

          <!-- custom dropdown -->
          <div
            v-if="showClientCustomerLocation"
            class="rounded-lg flex flex-col items-center justify-center px-4 py-3.5 border cursor-pointer"
          >
            <el-input
              placeholder="Search"
              prefix-icon="el-icon-search"
              class="mx-4 mt-1"
              v-model.trim="searchSupportedFiatTerm"
              debounce
            />
            <!-- currency option -->
            <div class="w-full h-24 overflow-scroll">
              <el-checkbox-group
                v-model="kycDetailsForm.clientCustomerLocation"
              >
                <el-checkbox
                  class="flex items-center mt-6 relative"
                  v-for="(fiat, index) in filteredSupportedFiats"
                  :key="index"
                  :label="fiat.shortCode"
                >
                  <div class="flex items-center justify-between w-full">
                    <p class="flex items-center gap-x-2">
                      <currency-icons :currency="fiat.name" />
                      <span class="uppercase">{{ fiat.name }}</span>
                    </p>
                    <p class="absolute right-3 top-0 uppercase">
                      {{ fiat.currency }}
                    </p>
                  </div>
                </el-checkbox>
              </el-checkbox-group>
            </div>
          </div>

          <div
            class="w-full flex flex-col md:flex-row md:gap-6 md:justify-between"
          >
            <el-form-item prop="countryOfIncorporation" class="w-full md:w-1/2">
              <p>
                Country of Incorporation <span class="text-error-10">*</span>
              </p>
              <el-select
                class="w-full"
                filterable
                remote
                v-model="kycDetailsForm.countryOfIncorporation"
                placeholder="Search"
                :remote-method="remoteMethod"
              >
                <el-option
                  v-for="(country, index) in options"
                  :key="index"
                  :label="country.name"
                  :value="country.code2"
                >
                  {{ country.name }}
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item prop="taxNumber" class="w-full md:w-1/2">
              <p>Tax Number / EIN <span class="text-error-10">*</span></p>
              <el-input
                @keydown.space.native.prevent
                v-model.trim="kycDetailsForm.taxNumber"
              ></el-input>
            </el-form-item>
          </div>

          <div>
            <el-form-item v-if="isBusinessTypeSME">
              <p>Website</p>
              <el-input
                class="w-full"
                v-model="kycDetailsForm.website"
              ></el-input>
            </el-form-item>

            <el-form-item v-else prop="website">
              <p>Website <span class="text-error-10">*</span></p>
              <el-input
                class="w-full"
                v-model="kycDetailsForm.website"
              ></el-input>
            </el-form-item>
          </div>
          <el-form-item
            :prop="natureOfBusinessDO === 'other' ? '' : 'natureOfBusiness'"
          >
            <p>Nature of Business</p>
            <el-select
              class="w-full"
              v-model="natureOfBusinessDO"
              placeholder="Select"
              @change="handleNatureOfBusinessSelection"
            >
              <el-option
                v-for="(businessType, index) in businessTypes"
                :key="index"
                :label="businessType.label"
                :value="businessType.value"
              >
                {{ businessType.label }}
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            prop="natureOfBusiness"
            v-if="natureOfBusinessDO === 'other'"
          >
            <el-input
              class="w-full"
              v-model="natureOfBusinessDI"
              @input="handleNatureOfBusinessSelection2"
            ></el-input>
          </el-form-item>

          <button
            class="text-white bg-primary rounded-lg mt-2 h-12 md:h-14 w-44 md:w-52 hover:opacity-90"
            type="submit"
            :disabled="isLoading || isApproved"
          >
            <spinner
              v-if="isLoading"
              size="small"
              :spacing="40"
              :line-fg-color="$primaryColor"
            />
            <span v-else>Confirm</span>
          </button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import {
  ALL_COUNTRIES,
  COMPANY_TYPES,
  URL_REGEX,
  FIAT
} from '@/utils/constants.js'
import { ValidateKycInput, GetCurrencyCountry } from '@/utils/helpers.js'
import kyc from '@/mixins/kyc.js'
import tenant from '@/mixins/tenant.js'

export default {
  name: 'BusinessProfile',
  mixins: [kyc, tenant],
  components: {
    DisputeNotification: () =>
      import('@/components/v2/completeKycForm/common/DisputeNotification.vue'),
    AttentionIcon: () => import('@/assets/v2/icons/AttentionIcon.vue'),
    ArrowIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue')
  },
  props: {
    business: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    var validateUrl = (rule, value, callback) => {
      if (!URL_REGEX.test(value) && this.kycDetailsForm.website !== '') {
        callback(new Error('Please input a valid URL'))
      } else {
        callback()
      }
    }
    return {
      companyTypes: COMPANY_TYPES,
      otherCompanyType: false,
      isLoading: false,
      options: [],
      allCountries: ALL_COUNTRIES,
      kycStep: 1,
      pickerOptions: {
        disabledDate: this.disabledDate
      },
      kycDetailsForm: {
        accountName: '',
        incorporationNumber: '',
        incorporationDate: '',
        countryOfIncorporation: '',
        taxNumber: '',
        natureOfBusiness: '',
        website: '',
        unlistedCompanyType: '',
        entityIsRegulated: false,
        clientCustomerLocation: [],
        regulatoryBodyName: '',
        previousEntityName: ''
      },
      natureOfBusinessDO: '',
      natureOfBusinessDI: '',
      rules: {
        accountName: [
          {
            required: true,
            message: 'Please input account name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        website: [
          {
            required: true,
            message: 'Please input website',
            trigger: ['blur', 'change']
          },
          { validator: validateUrl, trigger: ['blur', 'change'] }
        ],
        natureOfBusiness: [
          {
            required: false,
            message: 'Please input nature of business',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        registrationType: [
          {
            required: true,
            message: 'Please input registration type',
            trigger: ['blur', 'change']
          }
        ],
        unlistedCompanyType: [
          {
            required: true,
            message: 'Please input registration type',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        taxNumber: [
          {
            required: true,
            message: 'Please input tax id number',
            trigger: ['blur', 'change']
          },
          { min: 2, max: 23, message: 'Invalid tax number', trigger: 'blur' }
        ],
        incorporationDate: [
          {
            required: true,
            message: 'Please input incorporation date',
            trigger: ['blur', 'change']
          }
        ],
        countryOfIncorporation: [
          {
            required: true,
            message: 'Please input country of incorporation',
            trigger: ['blur', 'change']
          }
        ],
        incorporationNumber: [
          {
            required: true,
            message: 'Please input incorporation number',
            trigger: ['blur', 'change']
          },
          {
            max: 18,
            message: 'Invalid Input',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        clientCustomerLocation: [
          {
            required: true,
            message: 'Please input clients/customers location',
            trigger: ['blur', 'change']
          }
        ],
        regulatoryBodyName: [
          {
            required: true,
            message: 'Please enter regulatory body name',
            trigger: ['blur', 'change']
          }
        ]
      },
      supportedFiats: FIAT,
      showClientCustomerLocation: false,
      searchSupportedFiatTerm: ''
    }
  },
  computed: {
    tenantLabel () {
      return this.tenantTitle
    },
    showDetails () {
      return this.business.show && this.business.step === this.kycStep
    },
    businessInfo () {
      return this.$store.state.kyc.kycDetails
    },
    isApproved () {
      return this.approvedStages.includes(this.business.step)
    },
    filteredSupportedFiats () {
      if (!this.searchSupportedFiatTerm.trim()) return this.supportedFiats

      const searchTerm = this.searchSupportedFiatTerm.trim().toLowerCase()

      return this.supportedFiats.filter((currency) => {
        const {
          name,
          symbol,
          currency: code,
          currencyFull,
          shortCode
        } = currency

        return [name, symbol, code, currencyFull, shortCode].some((field) =>
          field?.toLowerCase().includes(searchTerm)
        )
      })
    },
    businessTypes () {
      return [
        {
          label: 'Retail',
          value: 'retail'
        },
        {
          label: 'Finance',
          value: 'finance'
        },
        {
          label: 'Healthcare',
          value: 'healthcare'
        },
        {
          label: 'Technology',
          value: 'technology'
        },
        {
          label: 'Education',
          value: 'education'
        },
        {
          label: 'Gaming',
          value: 'gaming'
        },
        {
          label: 'Fashion',
          value: 'fashion'
        },
        {
          label: 'Lending',
          value: 'lending'
        },
        {
          label: 'Other',
          value: 'other'
        }
      ]
    }
  },
  methods: {
    getCountryFullName (shortCode) {
      return (
        this.supportedFiats.find((country) => country.shortCode === shortCode)
          ?.name || ''
      )
    },
    removeLocationHandler (location) {
      this.kycDetailsForm.clientCustomerLocation =
        this.kycDetailsForm.clientCustomerLocation.filter(
          (clientLocation) => clientLocation !== location
        )
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    validateExpectedTransactionCurrency () {
      // eslint-disable-next-line no-unused-expressions
      this.$refs.kycDetailsForm?.validateField('clientCustomerLocation')
    },
    toggleClientCustomerLocation () {
      this.showClientCustomerLocation = !this.showClientCustomerLocation
    },
    handleNatureOfBusinessSelection () {
      if (this.natureOfBusinessDO !== 'other') {
        this.kycDetailsForm.natureOfBusiness = this.natureOfBusinessDO
      } else {
        this.kycDetailsForm.natureOfBusiness = ''
      }
    },
    handleNatureOfBusinessSelection2 () {
      this.kycDetailsForm.natureOfBusiness = this.natureOfBusinessDI
    },
    changeCompanyType () {
      if (this.kycDetailsForm.registrationType === 'others') {
        this.otherCompanyType = true
      } else {
        this.otherCompanyType = false
      }
    },
    validateForm (kycDetailsForm) {
      this.$refs[kycDetailsForm].validate((valid) => {
        if (valid) {
          this.submitKycStepOne()
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    submitKycStepOne () {
      const {
        unlistedCompanyType,
        entityIsRegulated,
        regulatoryBodyName,
        clientCustomerLocation,
        ...otherDetails
      } = this.kycDetailsForm

      const businessProfile = {
        ...otherDetails,
        isEntityRegulated: entityIsRegulated || 'no',
        regulatoryBody: regulatoryBodyName,
        customerLocations: clientCustomerLocation
      }

      const payload = {
        kycDetailsForm: { businessProfile },
        kycStep: this.kycStep
      }

      this.submitKycDetailsStep(payload)
    },
    prefillForm () {
      const business = this.businessInfo.contacts.find(
        (item) => item.contactType === 'company'
      )
      this.kycDetailsForm = {
        ...this.kycDetailsForm,
        clientCustomerLocation: business.customerLocations || [],
        registrationType: business.registrationType || '',
        incorporationNumber: business.incorporationNumber || '',
        incorporationDate: business.incorporationDate || '',
        countryOfIncorporation: business.countryOfIncorporation || '',
        taxNumber: business.taxNumber || '',
        natureOfBusiness: business.natureOfBusiness || '',
        unlistedCompanyType: business.unlistedCompanyType || '',
        entityIsRegulated: business?.isEntityRegulated,
        regulatoryBodyName: business?.regulatoryBody,
        previousEntityName: business?.previousEntityName
      }
      this.natureOfBusinessDO = business.natureOfBusiness
    },
    prefillExistingDetails () {
      this.kycDetailsForm.website = this.$store.state.auth.business.websiteUrl
      this.kycDetailsForm.accountName = this.$store.state.auth.business.name
      this.kycDetailsForm.companyType =
        this.$store.state.auth.business.businessType
    }
  },
  watch: {
    'kycDetailsForm.clientCustomerLocation': {
      handler: function () {
        this.$nextTick(() => {
          this.validateExpectedTransactionCurrency()
        })
      },
      immediate: true
    }
  },
  mounted () {
    if (this.businessInfo.contacts && this.businessInfo.contacts.length > 0) {
      this.prefillForm()
    }
    this.prefillExistingDetails()
    this.validateExpectedTransactionCurrency()
  }
}
</script>

<style scoped lang="scss">
p {
  color: #5d5e68;
  font-size: 13px;
}

button:disabled {
  opacity: 0.6;
  cursor: default;
}
:deep(.el-checkbox__inner) {
  height: 1.3rem;
  width: 1.3rem;
}
:deep(.el-checkbox__inner::after) {
  height: 0.6rem;
  left: 0.4rem;
  border-width: 2px;
  top: 0.15rem;
}
:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: var(--color-primary);
}
</style>
